import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { CreateAssetForm } from '../../components/activos/assets/CreateAssetForm';
import { useModalStore } from '../../store/useModalStore';
import { SocketContext } from '../../contexts/SocketContext';
import { AlertContext } from '../../contexts/AlertContext';
import { LoaderContext } from '../../contexts/LoaderContext';
import { convertirLetraMayus } from '../../helpers/convertirLetraMayus';
import notAvailableImage from '../../assets/img/placeholder.png';
import { Actions } from '../../components/activos/assets/Actions';
import dayjs from 'dayjs';
import { useReactToPrint } from 'react-to-print';
import { Check } from '../../components/activos/assets/Check';
import { GenerateAssetsForm } from '../../components/activos/assets/GenerateAssetsForm';

export const useAssets = () => {

    const [assets, setAssets] = useState([]);
    const [qrPrint, setQrPrint] = useState([]);
    const [dataFormat, setDataFormat] = useState({});
    const qrPrintRef = useRef();
    const assetsPrintRef = useRef();
    const { openModal, closeModal } = useModalStore();
    const { socket } = useContext(SocketContext);
    const { alertRef } = useContext(AlertContext);
    const { setOpenLoader } = useContext(LoaderContext);

    let columns = [
        { id: "check", label: assets.length > 1 && <Check checked={ (qrPrint.length > 1) && true } onChange={ (e) => changeCheckBoxAll(e) } />, minWidth: 70, align: 'center' },
        { id: "imagen", label: "", minWidth: 70, align: 'center' },
        { id: "placa", label: "Placa", minWidth: 70, align: 'center' },
        { id: "descripcion", label: "Descripción", minWidth: 100, align: 'center' },
        { id: "categoria", label: "Categoría", minWidth: 100, align: 'center' },
        { id: "responsable", label: "Responsable", minWidth: 140, align: 'center' },
        { id: "compra", label: "Fecha de compra", minWidth: 100, align: 'center' },
        { id: "acciones", label: "Acciones", minWidth: 250, align: 'center' }
    ];

    useEffect(() => {
        let subscribed = true;

        subscribed && getAssets();

        return () => subscribed = false;
    }, []);

    const searchAsset = (value) => { 
        setQrPrint([]);
        socket.emit('frontend:buscar-activo', { placa: value?.trim(), baja: false }, response => {
            const { ok, message, data } = response;

            if(!ok) return alertRef.current?.handleOpen('error', message);
          
            setAssets(data?.map(generatedPayload));
        });
    }

    const getAssets = useCallback(() => {
        setOpenLoader(true);
        socket.emit('frontend:obtener-activos', { "baja.estado": false }, response => {
            const { ok, message, data } = response;

            if(!ok) {
                setOpenLoader(false);
                alertRef?.current?.handleOpen('error', message);
                return;
            }

            setAssets(data?.map(generatedPayload));
            setOpenLoader(false);
        });
    }, []);

    const changeCheckBoxOne = (checked, data) => {
        if(checked) {
            setQrPrint(prev => ([... prev, { _id: data?._id, img: data?.qr?.secure_url, placa: data?.placa }]));
            setAssets(prev => prev?.map(p => p?._id === data?._id ? ({ ...p, check: <Check key={ p?._id } checked={ checked } onChange={ (e) => changeCheckBoxOne(e, p) } /> }) : p));

            return;
        }
        
        setQrPrint(prev => prev?.filter(p => p?._id !== data?._id));
        setAssets(prev => prev?.map(p => p?._id === data?._id ? ({ ...p, check: <Check key={ p?._id } checked={ checked } onChange={ (e) => changeCheckBoxOne(e, p) } /> }) : p));
    }

    const changeCheckBoxAll = (checked) => {
        if(checked) {
            setQrPrint(assets?.map(a => ({ _id: a?._id, img: a?.qr?.secure_url, placa: a?.placa })));
            setAssets(prev => prev?.map(p => ({ ...p, check: <Check key={ p?._id } checked={ checked } onChange={ (e) => changeCheckBoxOne(e, p) } /> })));

            return;
        }
        
        setQrPrint([]);
        setAssets(prev => prev?.map(p => ({ ...p, check: <Check key={ p?._id } checked={ checked } onChange={ (e) => changeCheckBoxOne(e, p) } /> })));
    }

    const printQrAssets = useReactToPrint({
        content: () => qrPrintRef?.current,
    });

    const printAssets = useReactToPrint({
        content: () => assetsPrintRef?.current,
    });

    const generateAssetForm = () => openModal(<GenerateAssetsForm action={ getAssetsByUser } />, 450);

    const getAssetsByUser = ({ usuario, observaciones }) => {
        setOpenLoader(true);
        socket.emit('frontend:obtener-activos-por-usuario', { usuario }, response => {
            const { ok, message, total, data } = response;

            if(!ok) {
                closeModal();
                setOpenLoader(false);
                alertRef?.current?.handleOpen('error', message);
                return;
            }

            if(total <= 0) {
                setOpenLoader(false);
                alertRef?.current?.handleOpen('error', 'No se encontraron activos vinculados al colaborador.');
                return;
            }

            setDataFormat({ observaciones, data });
            closeModal();
            setOpenLoader(false);
            printAssets();
        });
    }

    const addAsset = () => openModal(<CreateAssetForm action={ (data) => saveAsset(data)  } />);

    const saveAsset = (args) => {
        setOpenLoader(true);
        const { descripcion, ubicacion, placa, codigo_inteligente, fecha_compra, observacion, ...rest } = args;

        const payload = {
            descripcion: descripcion?.toLowerCase()?.trim(),
            ubicacion: ubicacion?.toLowerCase()?.trim(),
            placa: placa?.toString()?.trim(),
            codigo_inteligente: codigo_inteligente?.toString()?.toLowerCase()?.trim(),
            fecha_compra: String(fecha_compra)?.toLowerCase().trim(),
            observacion: observacion?.toLowerCase()?.trim(),
            ...rest
        }

        socket.emit('frontend:crear-activo', payload, response => {
            const { ok, message, data } = response;

            if(!ok) {
                setOpenLoader(false);
                alertRef?.current?.handleOpen('error', message);
                return;
            }

            setAssets((prev) => ([ ...prev, generatedPayload(data) ]));
            closeModal();
            setOpenLoader(false);
        });
    }

    const updateAsset = (args, _id) => {
        setOpenLoader(true);
        const { descripcion, ubicacion, placa, _idResponsable, observacion, ...rest } = args;
       
        const payload = {
            _id,
            descripcion: descripcion?.toLowerCase()?.trim(),
            ubicacion: ubicacion?.toLowerCase()?.trim(),
            placa: String(placa)?.toLowerCase()?.trim(),
            _idResponsable,
            observacion: observacion?.toLowerCase()?.trim(),
            ...rest
        }

        socket.emit('frontend:actualizar-activo', payload, response => {
            const { ok, message, data } = response;

            if(!ok) {
                setOpenLoader(false);
                alertRef?.current?.handleOpen('error', message);
                return;
            }

            setAssets(prev => prev?.map(a => a?._id === _id ? generatedPayload(data) : a ));
            closeModal();
            setOpenLoader(false);
        });
    }

    const deleteAsset = (_id ) => {
        setOpenLoader(true);
        const payload = { _id }

        socket.emit('frontend:eliminar-activo', payload, response => {
            const { ok, message } = response;

            if(!ok) {
                setOpenLoader(false);
                alertRef?.current?.handleOpen('error', message);
                return;
            }

            setAssets(prev => prev?.filter(a => a?._id !== _id));
            closeModal();
            setOpenLoader(false);
        });
    }

    const offActivo = async (fields, _id, responsable) => {
        setOpenLoader(true);
        const { motivo, disposicion, ...rest } = fields;

        const payload = {
            _id,
            responsable,
            motivo: motivo?.toLowerCase()?.trim(),
            disposicion: disposicion?.toLowerCase()?.trim(),
            ...rest
        }

        socket.emit('frontend:baja-activo', payload, response => {
            const { ok, message, data } = response;

            if(!ok) {
                setOpenLoader(false);
                alertRef?.current?.handleOpen('error', message);
                return;
            }

            setAssets(prev => prev?.filter(p => p?._id !== _id)?.map(a => a?._id === _id ? generatedPayload(data) : a ));
            closeModal();
            setOpenLoader(false);
        });
    }

    const reAssignUser = (field, _id, actualResponsable) => {
        setOpenLoader(true);
        const { responsable } = field;

        const payload = { _id, responsable, actualResponsable }

        socket.emit('frontend:asignar-activo', payload, response => {
            const { ok, message, data } = response;

            if(!ok) {
                setOpenLoader(false);
                alertRef?.current?.handleOpen('error', message);
                return;
            }

            setAssets(prev => prev?.map(a => a?._id === _id ? generatedPayload(data) : a ));
            closeModal();
            setOpenLoader(false);
        });
    }

    const generatedPayload = (data) => ({
        ...data,
        check: <input style={{ cursor: 'pointer' }} checked={ data?.checked } key={ data?._id } type='checkbox' onChange={ (e) => changeCheckBoxOne(e?.target?.checked, data) } />,
        imagen: <img src={ data?.imagen?.secure_url ?? notAvailableImage } alt='image' style={{ width: 40, height: 40, borderRadius: '50%' }} />,
        placa: data?.placa,
        descripcion: convertirLetraMayus(data?.descripcion),
        categoria: convertirLetraMayus(data?.categoria?.nombre),
        responsable: Object.values(data?.responsable) <= 0 ? 'N/A' : convertirLetraMayus(data?.responsable?.usuario?.nombre, true),
        compra: dayjs(data?.fecha_compra).format('DD-MM-YYYY'),
        acciones: <Actions data={ data } updated={ updateAsset } deleted={ deleteAsset } assign={ reAssignUser } off={ offActivo } />
    });

    return {
        columns,
        assets,
        addAsset,
        qrPrint,
        printQrAssets,
        qrPrintRef,
        assetsPrintRef,
        generateAssetForm,
        searchAsset,
        dataFormat
    }
}